import Microgame from '../engine/Microgame.js';

export default class TugGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Tire à la corde !`, en: `Tug of War!`, es: `¡Tira y afloja!` },
            controls: { fr: `Tape très vite pour tirer la corde`, en: `Tap very fast to pull the rope`, es: `Toca muy rápido para tirar de la cuerda` },
            objective: { fr: `Sois le plus fort et amène l'adversaire de ton côté`, en: `Be the strongest and pull the opponent to your side`, es: `Sé el más fuerte y arrastra al oponente a tu lado` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M3 12h18"/><circle cx="12" cy="12" r="3"/><path d="m8 8-2 4 2 4M16 8l2 4-2 4"/></svg>`,
            actions: [
                { type: 'tap', x: 50, y: 55, duration: 90 },
                { type: 'wait', duration: 90 },
                { type: 'tap', x: 50, y: 55, duration: 90 },
                { type: 'wait', duration: 90 },
                { type: 'tap', x: 50, y: 55, duration: 90 },
                { type: 'wait', duration: 90 },
                { type: 'tap', x: 50, y: 55, duration: 90 },
                { type: 'wait', duration: 90 },
                { type: 'tap', x: 50, y: 55, duration: 90 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.position = 50; // Center
        this.winThreshold = 80;
        this.loseThreshold = 20;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;
        // Map x4 difficulty to the old x3.5 to soften the peak
        const effectiveMult = 1 + (speedMult - 1) * (2.5 / 3);

        // Target taps needed to win: scaled down for much easier gameplay
        const tapsNeeded = 3 + (effectiveMult * 1.5);
        this.pullPerTap = (this.winThreshold - 50) / tapsNeeded;

        // Enemy strength: heavily reduced so it doesn't instantly overpower the player
        this.enemyStrength = 4 * effectiveMult; // pulls back % per second

        // Duration: Give generous time, we want the race to be intense
        const adjustedSpeed = 1 + (effectiveMult - 1) * (1.2 / 2.5);
        this.duration = 4000 * (speedMult / adjustedSpeed);

        this.flagOffset = 0; // Visual shaking
    }

    setup() {
        this.engine.ui.instruction.textContent = "TUG WAR!";

        // Background atmosphere
        const bg = document.createElement('div');
        bg.style.position = 'absolute';
        bg.style.width = '100%';
        bg.style.height = '100%';
        bg.style.background = `radial-gradient(ellipse at center, transparent 0%, ${this.secondaryColor} 100%)`;
        bg.style.opacity = '0.4';
        this.container.appendChild(bg);

        // Win Line (Right side)
        this.winLine = document.createElement('div');
        this.winLine.style.position = 'absolute';
        this.winLine.style.left = `${this.winThreshold}%`;
        this.winLine.style.top = '20%';
        this.winLine.style.bottom = '20%';
        this.winLine.style.width = '4px';
        this.winLine.style.background = this.primaryColor;
        this.winLine.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.winLine.style.transform = 'translateX(-50%)';
        this.winLine.style.borderRadius = '4px';
        this.container.appendChild(this.winLine);

        // Lose Line (Left side) - Uses secondary color instead of red
        this.loseLine = document.createElement('div');
        this.loseLine.style.position = 'absolute';
        this.loseLine.style.left = `${this.loseThreshold}%`;
        this.loseLine.style.top = '20%';
        this.loseLine.style.bottom = '20%';
        this.loseLine.style.width = '4px';
        this.loseLine.style.background = this.secondaryColor;
        this.loseLine.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
        this.loseLine.style.transform = 'translateX(-50%)';
        this.loseLine.style.borderRadius = '4px';
        this.container.appendChild(this.loseLine);

        // Rope Container (for sliding)
        this.ropeContainer = document.createElement('div');
        this.ropeContainer.style.position = 'absolute';
        this.ropeContainer.style.width = '100%';
        this.ropeContainer.style.height = '20px';
        this.ropeContainer.style.top = '50%';
        this.ropeContainer.style.left = '0';
        this.ropeContainer.style.transform = 'translateY(-50%)';
        this.container.appendChild(this.ropeContainer);

        // Actual Rope (very wide so it doesn't show edges)
        this.rope = document.createElement('div');
        this.rope.style.position = 'absolute';
        this.rope.style.width = '300%'; // Super wide
        this.rope.style.height = '14px';
        this.rope.style.left = '-100%';
        this.rope.style.top = '50%';
        this.rope.style.transform = 'translateY(-50%)';
        // Cool braided rope pattern! strictly using secondary and transparent
        this.rope.style.background = `repeating-linear-gradient(
            45deg,
            ${this.secondaryColor},
            ${this.secondaryColor} 10px,
            transparent 10px,
            transparent 20px
        )`;
        this.rope.style.borderRadius = '7px';
        this.rope.style.border = `2px solid ${this.secondaryColor}`; // Outline to make it pop since we removed shadows
        this.ropeContainer.appendChild(this.rope);

        // Flag Marker
        this.flagWrapper = document.createElement('div');
        this.flagWrapper.style.position = 'absolute';
        this.flagWrapper.style.left = '50%';
        this.flagWrapper.style.top = '50%';
        this.flagWrapper.style.width = '0';
        this.flagWrapper.style.height = '0';
        this.ropeContainer.appendChild(this.flagWrapper);

        // The knot
        const knot = document.createElement('div');
        knot.style.position = 'absolute';
        knot.style.width = '24px';
        knot.style.height = '24px';
        knot.style.background = this.primaryColor;
        knot.style.top = '50%';
        knot.style.left = '50%';
        knot.style.transform = 'translate(-50%, -50%)';
        knot.style.borderRadius = '50%';
        knot.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.flagWrapper.appendChild(knot);

        // The cloth
        this.cloth = document.createElement('div');
        this.cloth.style.position = 'absolute';
        this.cloth.style.width = '30px';
        this.cloth.style.height = '40px';
        this.cloth.style.background = `linear-gradient(to bottom, ${this.primaryColor}, transparent)`;
        this.cloth.style.top = '0';
        this.cloth.style.left = '50%';
        this.cloth.style.transform = 'translateX(-50%)';
        this.cloth.style.borderRadius = '4px 4px 0 0';
        this.flagWrapper.appendChild(this.cloth);

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);

        this.updateVisuals();
    }

    updateVisuals() {
        // Position goes from 0 to 100, mapped natively to percentage of screen width
        this.flagWrapper.style.left = `${this.position}%`;

        // Also slide the rope texture nicely
        const ropeSlide = (this.position - 50) * -1; // Move opposite to seem infinite
        this.rope.style.transform = `translateY(-50%) translateX(${ropeSlide}%)`;

        // Intensity styling logic without using hard bounds or new colors
        if (this.position > 65) {
            this.winLine.style.boxShadow = `0 0 30px ${this.primaryColor}`;
            this.winLine.style.filter = 'brightness(1.5)';
        } else {
            this.winLine.style.boxShadow = `0 0 15px ${this.primaryColor}`;
            this.winLine.style.filter = 'none';
        }

        if (this.position < 35) {
            this.loseLine.style.boxShadow = `0 0 30px ${this.secondaryColor}`;
            this.loseLine.style.filter = 'brightness(1.5)';
        } else {
            this.loseLine.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
            this.loseLine.style.filter = 'none';
        }
    }

    update(dt) {
        if (!this.active || this.won) return;

        // Enemy gently pulls back
        this.position -= this.enemyStrength * (dt / 1000);

        // Shake the flag slightly based on struggle
        this.flagOffset = (Math.random() - 0.5) * 4;
        this.cloth.style.transform = `translateX(calc(-50% + ${this.flagOffset}px)) rotate(${this.flagOffset * 2}deg)`;

        this.updateVisuals();

        if (this.position >= this.winThreshold) {
            this.win();
            this.hideTimer = true;
            this.winLine.style.width = '8px';
            this.winLine.style.filter = 'brightness(2)';
        } else if (this.position <= this.loseThreshold) {
            this.lose();
        }
    }

    onDown() {
        if (!this.active || this.won) return;
        this.position += this.pullPerTap;

        // Visual pull impact
        this.ropeContainer.style.transform = `translateY(-50%) scale(1.05)`;
        this.cloth.style.filter = 'brightness(1.5)';

        setTimeout(() => {
            if (this.active) {
                this.ropeContainer.style.transform = `translateY(-50%) scale(1)`;
                this.cloth.style.filter = 'none';
            }
        }, 50);

        this.updateVisuals();
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
